import { useEffect, useState } from 'react';
import { LoadingButton } from '@mui/lab';
import { TextField } from '@mui/material';
import { sendServerRequest } from '../../../server/request';
import { openGlobalSnack } from '../../utils/snack';
import { SelectFromItems } from '../../utils/forms/select';
import { URLS } from '../../../env/urls';
import { getServerStates, statesForScript, ServerStates, ScriptKey } from '../../../utils/fgtschat/serverstates';

const SCRIPT_ITEMS = [
  { value: 'script1', label: 'FGTS (Script 1)' },
  { value: 'script2', label: 'CLT (Script 2)' },
];

/** Extrai a mensagem de erro do JSONResponse ({data, hasError, error}). */
function parseError(err: any, fallback: string): string {
  try {
    const txt = err?.target?.responseText;
    if (txt) {
      const j = JSON.parse(txt);
      if (j?.error && typeof j.error === 'string') return j.error;
    }
  } catch (e) {}
  return fallback;
}

/**
 * Envia uma mensagem e muda o estado de TODOS os chats ativos que estão num
 * determinado estado. Se o script de destino for diferente do de origem, o bot
 * troca o script do chat junto (ex.: estava no FGTS e o próximo estado é do CLT).
 */
export function BulkStateBroadcast() {
  const [fromScript, setFromScript] = useState<ScriptKey>('script2');
  const [currentState, setCurrentState] = useState<string>('');
  const [toScript, setToScript] = useState<ScriptKey>('script2');
  const [nextState, setNextState] = useState<string>('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [lastResult, setLastResult] = useState<string>('');
  const [serverStates, setServerStates] = useState<ServerStates | undefined>(undefined);

  useEffect(() => {
    getServerStates().then(setServerStates);
  }, []);

  const statesOf = (script: ScriptKey): string[] =>
    serverStates ? statesForScript(serverStates, script) : [];

  return (
    <div style={{ maxWidth: 560, margin: '0 auto', textAlign: 'center' }}>
      <h3 style={{ marginBottom: 4 }}>Mensagem + mudança de estado em massa</h3>
      <p style={{ fontSize: 12, color: '#000000a0', marginTop: 0 }}>
        Envia a mensagem e move TODOS os chats ativos do estado atual para o próximo estado.
        Se o script de destino for diferente, o chat troca de script automaticamente.
      </p>

      <div style={{ marginTop: 16, textAlign: 'left' }}>
        <p style={{ fontWeight: 600, marginBottom: 6 }}>De (estado atual)</p>
        <div style={{ marginBottom: 10 }}>
          <SelectFromItems
            items={SCRIPT_ITEMS}
            defaultValue={fromScript}
            label="Script de origem"
            onChangeItem={(v) => {
              if (v !== 'script1' && v !== 'script2') return;
              setFromScript(v);
              setCurrentState('');
            }}
          />
        </div>
        <SelectFromItems
          items={statesOf(fromScript)}
          defaultValue={currentState}
          label="Estado atual"
          onChangeItem={(v) => setCurrentState(v)}
        />
      </div>

      <div style={{ marginTop: 24, textAlign: 'left' }}>
        <p style={{ fontWeight: 600, marginBottom: 6 }}>Para (próximo estado)</p>
        <div style={{ marginBottom: 10 }}>
          <SelectFromItems
            items={SCRIPT_ITEMS}
            defaultValue={toScript}
            label="Script de destino"
            onChangeItem={(v) => {
              if (v !== 'script1' && v !== 'script2') return;
              setToScript(v);
              setNextState('');
            }}
          />
        </div>
        <SelectFromItems
          items={statesOf(toScript)}
          defaultValue={nextState}
          label="Próximo estado"
          onChangeItem={(v) => setNextState(v)}
        />
      </div>

      <div style={{ marginTop: 24 }}>
        <TextField
          label="Mensagem"
          multiline
          minRows={3}
          fullWidth
          value={message}
          onChange={(e) => setMessage(e.target.value)}
        />
        <p style={{ fontSize: 12, color: '#000000a0', marginTop: 4 }}>
          Mensagem de texto enviada a cada chat afetado.
        </p>
      </div>

      <div style={{ marginTop: 16 }}>
        <LoadingButton loading={loading} variant="contained" onClick={submit}>
          Enviar e mudar estado
        </LoadingButton>
      </div>

      {lastResult ? (
        <p style={{ marginTop: 16, color: '#2e7d32' }}>{lastResult}</p>
      ) : null}
    </div>
  );

  function submit() {
    if (!currentState) { openGlobalSnack('Selecione o estado atual.', 'error'); return; }
    if (!nextState) { openGlobalSnack('Selecione o próximo estado.', 'error'); return; }
    if (!message.trim()) { openGlobalSnack('A mensagem é obrigatória.', 'error'); return; }

    const ok = window.confirm(
      `Isso vai enviar a mensagem e mudar o estado de TODOS os chats ativos em "${currentState}" para "${nextState}". Deseja continuar?`,
    );
    if (!ok) return;

    setLoading(true);
    setLastResult('');
    sendServerRequest('POST', URLS.bulkState.broadcast, {
      currentState,
      nextState,
      message: message.trim(),
      fromScript,
      toScript,
    }).then((e) => {
      setLoading(false);
      const matched = e.data?.data?.matched ?? 0;
      openGlobalSnack(`Processamento iniciado. Chats encontrados: ${matched}.`, 'success');
      setLastResult(`Chats encontrados: ${matched}. Envio e mudança de estado rodando em segundo plano.`);
    }).catch((err) => {
      setLoading(false);
      openGlobalSnack(parseError(err, 'Não foi possível iniciar o processamento.'), 'error');
    });
  }
}
